import { useRightPaneExpandContext } from "@/context/RightPaneExpandContext";
import { ResizablePanel } from "@cohesive/ui-components";
import { AnimatePresence, motion } from "motion/react";
import { useLayoutEffect, useRef, type ReactNode } from "react";
import {
  DOCKED_PANEL_CLASS,
  FLOATING_CHAT_DOCK_INITIAL_WIDTH_PX,
  FLOATING_CHAT_MIN_WIDTH_PX,
  FLOATING_CHAT_MOTION_INITIAL,
  FLOATING_CHAT_MOTION_TRANSITION,
  FLOATING_CHAT_REGION_VERTICAL_INSET_PX,
  MIN_EXPANDED_PANE_WIDTH_PX,
} from "./floatingChatShell";
import { useViewportSize } from "./useViewportSize";

/**
 * Full-height chat dock on the LEFT of a full-width-expanded detail pane (the
 * two-pane "read a full-screen file while chatting" layout). Fixed-positioned
 * against the content-region rect — the in-flow region is squeezed to ~the
 * dock's width, so `absolute` positioning is useless — and drag-resizable from
 * its right edge: the dock reports its live width as `leftChatDockPx`, the
 * expanded pane shrinks by exactly that, and the freed strip is what the dock
 * overlays. Shared by the canvas and home/workspace panels.
 */
export function FloatingChatLeftDock({
  anchorRect,
  storageKey,
  children,
}: {
  /** Content-region rect the dock anchors to (`left`/`top`/`height`). */
  anchorRect: DOMRect;
  /** Per-surface ResizablePanel width-persistence key. */
  storageKey: string;
  children: ReactNode;
}) {
  const shellRef = useRef<HTMLDivElement>(null);
  const { setLeftChatDockPx } = useRightPaneExpandContext();
  const { viewportWidthPx } = useViewportSize();

  // Cap the dock so the expanded pane beside it keeps a usable column.
  const maxWidthPx = Math.max(
    FLOATING_CHAT_MIN_WIDTH_PX,
    viewportWidthPx -
      anchorRect.left -
      MIN_EXPANDED_PANE_WIDTH_PX -
      FLOATING_CHAT_REGION_VERTICAL_INSET_PX,
  );

  // Report the dock's live width so the expanded pane shrinks to sit at its
  // right instead of being overlapped. offsetWidth is the layout width —
  // unaffected by the enter animation's scale transform (getBoundingClientRect
  // would read the scaled width).
  useLayoutEffect(() => {
    const el = shellRef.current;
    if (!el) return;
    const measure = () => setLeftChatDockPx(el.offsetWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      ro.disconnect();
      setLeftChatDockPx(0);
    };
  }, [setLeftChatDockPx]);

  return (
    <AnimatePresence initial={false}>
      <motion.div
        key="left-dock"
        ref={shellRef}
        data-floating-chat="left-dock"
        // A definite-height flex shell so the `h-full` panel fills vertically
        // (see DOCKED_SHELL_BASE_CLASS). No `overflow-hidden` — the right-edge
        // drag-handle pill overhangs the panel edge and would be clipped.
        className="fixed z-50 flex border-r border-n-10"
        style={{
          left: anchorRect.left,
          top: anchorRect.top,
          height: anchorRect.height,
        }}
        initial={FLOATING_CHAT_MOTION_INITIAL}
        animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
        exit={FLOATING_CHAT_MOTION_INITIAL}
        transition={FLOATING_CHAT_MOTION_TRANSITION}
      >
        <ResizablePanel
          // Width-only resize from the dock/pane boundary (right edge), with
          // the drag pill shown — same affordance as the RightPane dock.
          directions={["right"]}
          showHandleIndicator
          isClosed={false}
          transitionDuration={0}
          minWidth={FLOATING_CHAT_MIN_WIDTH_PX}
          maxWidth={maxWidthPx}
          initialWidth={FLOATING_CHAT_DOCK_INITIAL_WIDTH_PX}
          storageKey={storageKey}
          className={DOCKED_PANEL_CLASS}
        >
          {children}
        </ResizablePanel>
      </motion.div>
    </AnimatePresence>
  );
}
